import {useCallback, useRef} from 'react';

import {useFileDragDrop} from '../../hooks/useFileDragDrop';
import {cn} from '../../lib/utils';
import {AvatarPicker} from '../AvatarPicker/AvatarPicker';
import {Icon} from '../Icon/Icon';
import {Button} from '../shadcn/button';
import {FieldError} from '../shadcn/field';
import {Spinner} from '../shadcn/spinner';
import {Typography} from '../Typography/Typography';

import type {AvatarShape} from '../Avatar/avatar-variants';
import type {AvatarPickerHandle} from '../AvatarPicker/AvatarPicker';

export interface AvatarUploadProps {
  src?: string;
  initials: string;
  /** Deterministic color seed (e.g. the owner's user id) — matches how every other avatar in the app is colored. */
  seed?: string;
  /** `circle` for a personal avatar, `square` for a firm/brand logo. Defaults to `circle`. */
  shape?: AvatarShape;
  isUploading?: boolean;
  /** Fully read-only — also unfocusable, not just visually dimmed. */
  disabled?: boolean;
  error?: string;
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  titleLabel: string;
  uploadLabel: string;
  dragLabel: string;
  removeLabel: string;
  className?: string;
}

/**
 * Avatar editor: current avatar with a camera edit-badge, a drop strip, and a remove action.
 * Fully controlled and I/O-free — the caller owns upload state and object-URL previews.
 * Shared between the user's own account settings and firm settings (identical block, firm logo).
 */
export function AvatarUpload({
  src,
  initials,
  seed,
  shape = 'circle',
  isUploading = false,
  disabled = false,
  error,
  onFileSelect,
  onRemove,
  titleLabel,
  uploadLabel,
  dragLabel,
  removeLabel,
  className
}: AvatarUploadProps) {
  const pickerRef = useRef<AvatarPickerHandle>(null);
  const isInert = isUploading || disabled;
  const {isDragActive, dragProps} = useFileDragDrop({
    disabled: isInert,
    multiple: false,
    onFilesSelect: files => {
      const file = files[0];
      if (file) onFileSelect(file);
    }
  });

  const handlePick = useCallback(() => pickerRef.current?.open(), []);

  return (
    <div data-slot="avatar-upload" className={cn('flex items-start gap-4.5', className)}>
      <AvatarPicker
        ref={pickerRef}
        src={src}
        initials={initials}
        seed={seed}
        shape={shape}
        disabled={isInert}
        onFileSelect={onFileSelect}
        uploadLabel={uploadLabel}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <Typography size="sm" weight="semibold" color="secondary">
          {titleLabel}
        </Typography>

        <div
          data-slot="avatar-upload-dropzone"
          role="button"
          tabIndex={isInert ? -1 : 0}
          aria-disabled={isInert}
          onClick={handlePick}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handlePick();
            }
          }}
          {...dragProps}
          className={cn(
            'flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border bg-background px-4 py-3.5 text-fg-tertiary',
            isDragActive && 'border-brand-primary bg-background-surface_hover',
            isInert && 'cursor-not-allowed opacity-60'
          )}
        >
          {isUploading ? (
            <Spinner />
          ) : (
            <Icon name="upload" className="size-4.5 shrink-0 text-fg-quaternary" />
          )}
          <Typography size="sm" color="tertiary" className="whitespace-normal">
            <span className="font-bold text-brand-primary">{uploadLabel}</span> {dragLabel}
          </Typography>
        </div>

        {error && <FieldError className="whitespace-normal">{error}</FieldError>}

        {!error && src && onRemove && (
          <Button
            type="button"
            variant="linkColor"
            destructive
            size="xs"
            disabled={isInert}
            onClick={onRemove}
            className="self-start px-0"
          >
            {removeLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
